// ────────────────────────────────────────────────────────────────────────────────
// Open-Meteo Weather Service — 100% FREE, no API key, no limits
// https://open-meteo.com/
// ────────────────────────────────────────────────────────────────────────────────

export interface WeatherData {
  temperature: number;
  apparentTemperature: number;
  humidity: number;
  windSpeed: number;
  precipitation: number;
  weatherCode: number;
  isDay: boolean;
  daily: DailyForecast[];
}

export interface DailyForecast {
  date: string;
  tempMax: number;
  tempMin: number;
  precipitationProbability: number;
  weatherCode: number;
}

const CACHE_KEY = 'tonveon_weather_cache';
const CACHE_TTL = 30 * 60 * 1000; // 30 minutes

const WMO_CODES: Record<number, { label: string; icon: string }> = {
  0: { label: 'Despejado', icon: '☀️' },
  1: { label: 'Mayormente despejado', icon: '🌤️' },
  2: { label: 'Parcialmente nublado', icon: '⛅' },
  3: { label: 'Nublado', icon: '☁️' },
  45: { label: 'Niebla', icon: '🌫️' },
  48: { label: 'Niebla helada', icon: '🌫️' },
  51: { label: 'Llovizna ligera', icon: '🌦️' },
  53: { label: 'Llovizna moderada', icon: '🌦️' },
  55: { label: 'Llovizna intensa', icon: '🌧️' },
  61: { label: 'Lluvia ligera', icon: '🌧️' },
  63: { label: 'Lluvia moderada', icon: '🌧️' },
  65: { label: 'Lluvia intensa', icon: '🌧️' },
  71: { label: 'Nieve ligera', icon: '🌨️' },
  73: { label: 'Nieve moderada', icon: '🌨️' },
  75: { label: 'Nieve intensa', icon: '❄️' },
  80: { label: 'Chubascos ligeros', icon: '🌦️' },
  81: { label: 'Chubascos moderados', icon: '🌧️' },
  82: { label: 'Chubascos intensos', icon: '⛈️' },
  95: { label: 'Tormenta', icon: '⛈️' },
  96: { label: 'Tormenta con granizo', icon: '⛈️' },
  99: { label: 'Tormenta con granizo fuerte', icon: '⛈️' },
};

export function getWeatherInfo(code: number): { label: string; icon: string } {
  return WMO_CODES[code] || { label: 'Desconocido', icon: '❓' };
}

export function isRainyWeather(code: number): boolean {
  return code >= 51;
}

interface CacheEntry { data: WeatherData; ts: number; lat: number; lng: number; }

function getCached(lat: number, lng: number): WeatherData | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const entry: CacheEntry = JSON.parse(raw);
    if (Date.now() - entry.ts > CACHE_TTL) return null;
    if (Math.abs(entry.lat - lat) > 0.01 || Math.abs(entry.lng - lng) > 0.01) return null;
    return entry.data;
  } catch { return null; }
}

function setCache(lat: number, lng: number, data: WeatherData) {
  try {
    const entry: CacheEntry = { data, ts: Date.now(), lat, lng };
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch { /* quota exceeded — ignore */ }
}

export async function fetchWeather(lat: number, lng: number): Promise<WeatherData | null> {
  const cached = getCached(lat, lng);
  if (cached) return cached;

  try {
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=temperature_2m,apparent_temperature,relative_humidity_2m,wind_speed_10m,precipitation,weather_code,is_day&daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max,weather_code&timezone=auto&forecast_days=7`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const json = await res.json();

    const data: WeatherData = {
      temperature: json.current.temperature_2m,
      apparentTemperature: json.current.apparent_temperature,
      humidity: json.current.relative_humidity_2m,
      windSpeed: json.current.wind_speed_10m,
      precipitation: json.current.precipitation,
      weatherCode: json.current.weather_code,
      isDay: json.current.is_day === 1,
      daily: (json.daily.time as string[]).map((date: string, i: number) => ({
        date,
        tempMax: json.daily.temperature_2m_max[i],
        tempMin: json.daily.temperature_2m_min[i],
        precipitationProbability: json.daily.precipitation_probability_max[i],
        weatherCode: json.daily.weather_code[i],
      })),
    };

    setCache(lat, lng, data);
    return data;
  } catch {
    return null;
  }
}

/** Default location: Madrid, Spain */
export const DEFAULT_COORDS = { lat: 40.4168, lng: -3.7038 };
